/* eslint-disable camelcase */
import type {
  unstable_GqlError as GqlError,
  unstable_InternalOperation as InternalOperation,
} from '@sail/data';
/* eslint-enable camelcase */
import {unstable_DataNetworkError as DataNetworkError} from '@sail/data';

import SharedRestLink from '../../../../shared/graphql/links/RestLink/RestLink';
import type {
  ErrorHandlerStrategy,
  ResponseErrorsParams,
  MapUnionTypenamesErrorParams,
  ErrorCallbackParams,
} from '../../../../shared/graphql/links/RestLink/RestLink';
import type {InteropFields} from '../../../../shared/graphql/links/RestLink/rewriteInteropParams';
import type {
  UnionObjectTypesMap,
  FieldVersionOverrides,
} from '../../../../shared/graphql/links/RestLink/types';
import type {TopLevelFieldToArgs} from '../../../../shared/graphql/links/RestLink/removePbeFromParams';
import rebuildErrorFromResponse from './rebuildErrorFromResponse';
import GraphQlGatewayCompatibilityError from '../../../../shared/graphql/GraphQlGatewayCompatibilityError';

const isNetworkError = (error: GqlError) =>
  error.extensions.error.code === 'network_error';

const is5xxError = (error: GqlError) => error.extensions.error.status >= 500;

const ERROR_HANDLER: ErrorHandlerStrategy<GqlError, InternalOperation> = {
  handleResponseErrors: ({
    response,
    observer,
  }: ResponseErrorsParams<GqlError>) => {
    const firstNonGraphQlError = response.errors!.find(
      (error: GqlError) => isNetworkError(error) || is5xxError(error),
    );

    if (firstNonGraphQlError) {
      const error = new DataNetworkError(
        firstNonGraphQlError.message,
        firstNonGraphQlError.extensions.error.status,
        firstNonGraphQlError.extensions.unstable_requestId,
      );

      observer.error(error);

      return;
    }

    observer.next({
      data: response.data,
      errors: response.errors,
    });
  },

  handleMapUnionTypenamesError: ({
    error,
    observer,
  }: MapUnionTypenamesErrorParams<GqlError, InternalOperation>) => {
    observer.error(error);
  },
  handleErrorCallback: ({
    error,
    observer,
  }: ErrorCallbackParams<GqlError, InternalOperation>) => {
    if (error instanceof GraphQlGatewayCompatibilityError) {
      const graphQlError: GqlError = {
        message: error.message,
        path: error.path,
        extensions: {
          actionId: null,
          traceId: null,
          unstable_requestId: null,
          error: {
            code: 'graphql_gateway_compatibility_error',
            status: -1,
          },
        },
      };

      observer.next({
        data: null,
        errors: [graphQlError],
      });

      observer.complete();

      return;
    }

    observer.error(error);
    observer.complete();
  },
};

export default class RestLink extends SharedRestLink<
  GqlError,
  InternalOperation
> {
  constructor(
    unionObjectTypesMap: UnionObjectTypesMap,
    pbeMap: Record<string, ReadonlyArray<string>>,
    topLevelFieldToArgs: TopLevelFieldToArgs,
    fieldVersionOverrides: FieldVersionOverrides,
    queryEndpointsToBeRequestedWithPost?: string[],
    shadowComparisonOperationsV1ToV2?: Record<string, boolean>,
    interopFields?: InteropFields,
  ) {
    super(
      unionObjectTypesMap,
      pbeMap,
      topLevelFieldToArgs,
      fieldVersionOverrides,
      ERROR_HANDLER,
      rebuildErrorFromResponse,
      queryEndpointsToBeRequestedWithPost,
      shadowComparisonOperationsV1ToV2,
      interopFields,
    );
  }
}
